import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BreakdownElement,
  InsightsQueryKeys,
  OutstandingFeesBreakdown,
  OutstandingFeesType,
  fetchOutstandingFeesBreakdown,
} from 'services/clients/insights';
import { convertRelativeDate } from 'utils/common';
import moment, { Moment } from 'moment';
import { HistoricalPerDiemCalendarPeriod, useTrackedHistoricalParams } from '../params';
import { DayBreakdown } from './OverTimeSection/ChargesOverTime/types';

const dateFormat = 'YYYY-MM-DD';

const convertRelativeDateToString = (date: string) => {
  return (convertRelativeDate(date) as Moment).format(dateFormat);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const convertDateRangeSchemaToStartAndEnd = (dateRange: any) => {
  if (!dateRange) {
    return null;
  }

  if (dateRange.exactDate) {
    return {
      startDate: convertRelativeDateToString(dateRange.exactDate),
      endDate: convertRelativeDateToString(dateRange.exactDate),
    };
  }
  return {
    startDate: convertRelativeDateToString(dateRange.startDate),
    endDate: convertRelativeDateToString(dateRange.endDate),
  };
};

export const useCurrentDateRangeFilter = () => {
  const { searchParams } = useTrackedHistoricalParams();
  const { dateRange } = searchParams;

  return React.useMemo(() => {
    return convertDateRangeSchemaToStartAndEnd(dateRange);
  }, [dateRange]);
};

const useHistoricDateRangeFilter = () => {
  const { searchParams } = useTrackedHistoricalParams();
  const { dateRange, compare } = searchParams;

  const historicalOffset = React.useMemo(() => {
    const startDate = convertRelativeDate(dateRange.startDate) as Moment;
    const endDate = convertRelativeDate(dateRange.endDate) as Moment;

    return endDate.diff(startDate, 'days');
  }, [dateRange]);

  const historicalFilter = React.useMemo(() => {
    if (!compare || !dateRange) {
      return null;
    }

    if (dateRange.exactDate) {
      const date = (convertRelativeDate(dateRange.exactDate) as Moment)
        .subtract(1, 'days')
        .format(dateFormat);
      return {
        startDate: date,
        endDate: date,
      };
    }

    const startDate = convertRelativeDate(dateRange.startDate) as Moment;
    const endDate = convertRelativeDate(dateRange.endDate) as Moment;

    const historicalStartDate = startDate.subtract(historicalOffset, 'days');
    const historicalEndDate = endDate.subtract(historicalOffset, 'days');

    return {
      startDate: historicalStartDate.format(dateFormat),
      endDate: historicalEndDate.format(dateFormat),
    };
  }, [compare, dateRange, historicalOffset]);

  return { historicalFilter, historicalOffset };
};

interface OverTimeData {
  data: DayBreakdown[];
  isLoading: boolean;
  showHistoric: boolean;
  errors: Error[];
  calendarPeriod: HistoricalPerDiemCalendarPeriod;
}

export const useOverTimeData: () => OverTimeData = () => {
  const { searchParams } = useTrackedHistoricalParams();
  const { calendarPeriod } = searchParams;
  const currentFilter = useCurrentDateRangeFilter();

  const currentDataFilter = {
    type: OutstandingFeesType.PerDiem,
    calendarPeriod,
    ...currentFilter,
  };
  const currentData = useQuery({
    queryKey: [InsightsQueryKeys.OutstandingFeesBreakdown, currentDataFilter],
    queryFn: ({ signal }) => fetchOutstandingFeesBreakdown(currentDataFilter, signal),
    enabled: currentFilter !== null,
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error(error);
    },
  });

  const { historicalFilter, historicalOffset } = useHistoricDateRangeFilter();

  const historicalDataFilter = {
    type: OutstandingFeesType.PerDiem,
    calendarPeriod,
    ...historicalFilter,
  };
  const historicalData = useQuery({
    queryKey: [InsightsQueryKeys.OutstandingFeesBreakdown, historicalDataFilter],
    queryFn: ({ signal }) => {
      return fetchOutstandingFeesBreakdown(historicalDataFilter, signal);
    },
    enabled: historicalFilter !== null,
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error(error);
    },
  });

  const errors: Error[] = React.useMemo(() => {
    return [
      ...(currentData.error ? [currentData.error] : []),
      ...(historicalData.error ? [historicalData.error] : []),
    ] as Error[];
  }, [currentData.error, historicalData.error]);

  const isLoading = currentData.isLoading || (!!historicalFilter && historicalData.isLoading);

  return React.useMemo(() => {
    if (
      !currentData.data &&
      !historicalData.data &&
      !(currentData.isError && historicalData.isError)
    ) {
      return {
        data: [],
        isLoading,
        showHistoric: !!historicalFilter,
        calendarPeriod: calendarPeriod || HistoricalPerDiemCalendarPeriod.Day,
        errors,
      };
    }

    const records: Record<string, DayBreakdown> = {};

    if (currentData?.data) {
      currentData.data.forEach((e) => {
        if (!e.accrualDate) {
          return;
        }
        records[e.accrualDate] = {
          ...records[e.accrualDate],
          date: e.accrualDate,
          current: {
            disputable: e.disputableAmount,
            nonDisputable: e.amount - e.disputableAmount,
          },
        };
      });
    }

    if (historicalData?.data) {
      historicalData.data.forEach((e) => {
        if (!e.accrualDate) {
          return;
        }
        const matchedAccrualDate = moment(e.accrualDate)
          .add(historicalOffset, 'days')
          .format(dateFormat);

        records[matchedAccrualDate] = {
          ...records[matchedAccrualDate],
          date: matchedAccrualDate,
          historic: {
            disputable: e.disputableAmount,
            nonDisputable: e.amount - e.disputableAmount,
          },
        };
      });
    }

    return {
      data: Object.values(records).sort((a, b) => {
        return moment(a.date).diff(moment(b.date));
      }),
      isLoading,
      showHistoric: !!historicalFilter,
      calendarPeriod: calendarPeriod || HistoricalPerDiemCalendarPeriod.Day,
      errors,
    };
  }, [
    currentData,
    historicalData,
    historicalFilter,
    historicalOffset,
    calendarPeriod,
    errors,
    isLoading,
  ]);
};

interface CombinedBreakdownElement {
  label: string;
  key: string;
  current?: { amount: number; disputableAmount: number; numberOfContainers: number };
  historic?: { amount: number; disputableAmount: number; numberOfContainers: number };
}

interface BreakdownData {
  data: CombinedBreakdownElement[];
  isLoading: boolean;
  showHistoric: boolean;
  errors: Error[];
}

// Used for flattening breakdown elements with the same key
const flatten = (elements: BreakdownElement[]) => {
  const records: Record<string, BreakdownElement> = {};
  elements.forEach((element) => {
    const { key, daysCharged, amount, disputableAmount, numberOfContainers } = element;

    records[key] = {
      ...element,
      accrualDate: null,
      daysCharged: (records[key]?.daysCharged || 0) + daysCharged,
      amount: (records[key]?.amount || 0) + amount,
      disputableAmount: (records[key]?.disputableAmount || 0) + disputableAmount,
      numberOfContainers: (records[key]?.numberOfContainers || 0) + numberOfContainers,
    };
  });
  return records;
};

export const useBreakdownData: (breakdown: OutstandingFeesBreakdown) => BreakdownData = (
  breakdown
) => {
  // Get the date range filter for the current data and construct the request filter
  const currentDateRangeFilter = useCurrentDateRangeFilter();
  const currentBreakdownFilter = React.useMemo(() => {
    if (!currentDateRangeFilter) return null;
    return {
      type: OutstandingFeesType.PerDiem,
      breakdown,
      ...currentDateRangeFilter,
    };
  }, [breakdown, currentDateRangeFilter]);

  // Fetch the current data
  const currentData = useQuery({
    queryKey: [InsightsQueryKeys.OutstandingFeesBreakdown, currentBreakdownFilter],
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryFn: ({ signal }) => fetchOutstandingFeesBreakdown(currentBreakdownFilter!, signal),
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error(error);
    },
    enabled: !!currentBreakdownFilter,
  });

  // Get the date range filter for the historic data and construct the request filter if applicable
  const { historicalFilter: historicDateRangeFilter } = useHistoricDateRangeFilter();
  const historicBreakdownFilter = React.useMemo(() => {
    if (!historicDateRangeFilter) return null;
    return {
      type: OutstandingFeesType.PerDiem,
      breakdown,
      ...historicDateRangeFilter,
    };
  }, [breakdown, historicDateRangeFilter]);

  // Fetch the historic data
  const historicData = useQuery({
    queryKey: [InsightsQueryKeys.OutstandingFeesBreakdown, historicBreakdownFilter],
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryFn: ({ signal }) => fetchOutstandingFeesBreakdown(historicBreakdownFilter!, signal),
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error(error);
    },
    enabled: !!historicBreakdownFilter,
  });

  // Used for loading indication
  const isLoading = currentData.isLoading || (historicData.isLoading && !!historicBreakdownFilter);
  const hasData = !!currentData.data || !!historicData.data;

  const errors: Error[] = React.useMemo(() => {
    return [
      ...(currentData.error ? [currentData.error] : []),
      ...(historicData.error ? [historicData.error] : []),
    ] as Error[];
  }, [currentData.error, historicData.error]);

  // Once we have the data, begin to combine it into a format that the graph can use
  const data: CombinedBreakdownElement[] = React.useMemo(() => {
    if (!hasData) return [];

    const current = currentData?.data || [];
    const historic = historicData?.data || [];

    // Since the data is broken down by date, we need to flatten it into a single record for each key
    const currentRecords: Record<string, BreakdownElement> = flatten(current);
    const historicRecords: Record<string, BreakdownElement> = flatten(historic);

    // Combine the current and historic records into a single element that can be used to generate the rows for the graph
    const combinedRecords: Record<string, CombinedBreakdownElement> = {};
    Object.values(currentRecords).forEach((currentRecord) => {
      combinedRecords[currentRecord.key] = {
        ...combinedRecords[currentRecord.key],
        label: currentRecord.label,
        key: currentRecord.key,
        current: {
          amount: currentRecord.amount,
          disputableAmount: currentRecord.disputableAmount,
          numberOfContainers: currentRecord.numberOfContainers,
        },
      };
    });
    Object.values(historicRecords).forEach((historicRecord) => {
      combinedRecords[historicRecord.key] = {
        ...combinedRecords[historicRecord.key],
        label: historicRecord.label,
        key: historicRecord.key,
        historic: {
          amount: historicRecord.amount,
          disputableAmount: historicRecord.disputableAmount,
          numberOfContainers: historicRecord.numberOfContainers,
        },
      };
    });

    // Sort the combined records by the current amount and then generate the rows for the graph
    return Object.values(combinedRecords).sort(
      (a, b) =>
        (b.current?.amount || b.historic?.amount || 0) -
        (a.current?.amount || a.current?.amount || 0)
    );
  }, [currentData, historicData, hasData]);

  return {
    data,
    isLoading,
    showHistoric: !!historicBreakdownFilter,
    errors,
  };
};
